import React from 'react'
import { View } from '@components/atoms'

import { useAppSelector, CartType, CheckoutQuoteType } from '@redux/index'
import { Decimal, formatAmount, CartQuote, CartLineKey } from '@utils'
import { useDisplayCurrency } from '@components/systems/monolith/useDisplayCurrency'

// PLS 8 은 상점 목록(monolith/Shop.page)과 같은 컷 — 시즌상품 코드 PLS8 (PRODUCT_NAMES.md)
import pls8Image from '@public/images/monolith/koinos8-angle-full.png'
import kedem6Image from '@public/images/pages/shop/kedem6/thumbnail/kedem6-thumb2.jpg'
import kedem4Image from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb2.jpg'
import amp2Image from '@public/images/pages/shop/amp2/thumbnail/amp2-thumb2.jpg'
import dsImage from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery2.jpg'
import noImage from '@public/images/pages/shop/noimage.jpg'

const imageObj: { [code: string]: string } = {
	PLS8: pls8Image,
	KDM6: kedem6Image,
	KDM4: kedem4Image,
	AMP2: amp2Image,
	DS: dsImage,
}

/**
 * 결제 화면의 Items 블록 — Monolith(2안). 구 구현은 `systems/payment/PaymentItems.tsx`.
 *
 * 수량 합 계산은 구 구현 그대로다. grey[200] 회색 판과 5px 라운드 카드를 걷어내고
 * Cart 와 같은 헤어라인 행으로 세웠다 — 같은 물건을 두 화면에서 다르게 그리지 않는다.
 * 스타일은 페이지가 스프레드한 `paymentPageStyle` 을 그대로 물려받는다.
 *
 * 구 구현이 블록 끝에 달던 소계 행은 뺐다 — 바로 아래 Total 블록이 같은 줄("Subtotal · N
 * items")을 다시 그려서 한 화면에 같은 숫자가 두 번 서 있었다. 여기는 무엇을 사는지,
 * Total 은 얼마를 내는지로 나눈다.
 */
export const MonolithPaymentItems = () => {
	// redux state
	const cartList: CartType[] = useAppSelector(state => state.payment.tryToPayment)[
		'cartList'
	]
	// 줄 금액은 서버 견적(통화 포함)을 찍는다. 견적 전에는 장바구니의 USD 값으로 잠깐 그린다
	const quote: CheckoutQuoteType | null = useAppSelector(state => state.payment.quote)

	const vatSubtotal: {
		usd: number
		qty: number
	} = React.useMemo(() => {
		const res: {
			usd: number
			qty: number
		} = cartList.reduce(
			(sum, { sumUsd, vatUsd, qty }) => {
				return {
					usd: Decimal(sum.usd + sumUsd + vatUsd),
					qty: sum.qty + qty,
				}
			},
			{
				usd: 0,
				qty: 0,
			},
		)
		return res
	}, [cartList])

	/*
		줄 금액의 통화 — Cart 와 같은 규칙이다(`Cart.page` showKrw). 견적이 오면 견적의 통화,
		그 전에는 표시 통화로 담을 때 본 값을 그린다. 한국에서 온 방문자에게 달러가 한 번도
		스치지 않게 하려는 것이다 (PRICING §6 · §7.3). 담을 때 원화 단가를 못 받은 줄이
		하나라도 있으면(옛 장바구니) 견적 전에는 USD 로 그린다.
	*/
	const displayCurrency = useDisplayCurrency()
	const localKrwOk: boolean = cartList.every(anItem => (anItem.unitKrw ?? 0) > 0)
	const showKrw: boolean =
		quote !== null ? quote.currency === 'KRW' : displayCurrency === 'KRW' && localKrwOk
	/** 견적 전 줄 금액 — 담을 때 본 값(옵션 추가금 포함) */
	const localLine = (anItem: CartType): number =>
		showKrw
			? Math.round(CartQuote.localUnitKrw(anItem) * anItem.qty)
			: Decimal(anItem.sumUsd + anItem.vatUsd)

	return (
		<View className="mnPaySection">
			<View className="mnPaySectionHead">
				<span className="mnKicker">Items</span>
				<p className="mnPaySectionNote">
					{vatSubtotal.qty === 1
						? '1 item in this order.'
						: `${vatSubtotal.qty} items in this order.`}
				</p>
			</View>

			<View className="mnPaySectionBody">
				<View className="mnPayItemList">
					{cartList.map(anItem => {
						// 시즌상품 코드는 'pls8' 처럼 소문자로도 온다 — 대문자로 맞춰 찾는다
						const imageKey = (anItem.productCode ?? '').toUpperCase()
						const image = imageKey in imageObj ? imageObj[imageKey] : noImage

						return (
							<View key={CartLineKey.of(anItem)} className="mnPayItem">
								<View className="mnPayItemThumb">
									<img src={image} alt={anItem.productName} />
								</View>

								<View className="mnPayItemName">
									<span className="mnPayItemProduct">{anItem.productName}</span>
									{anItem.option?.id !== '' && (
										<span className="mnPayItemOption">{anItem.option?.name}</span>
									)}
									<span className="mnPayItemQty">{`×${anItem.qty}`}</span>
								</View>

								<span className="mnPayItemUsd">
									{(() => {
										const line = CartQuote.line(quote, anItem.id, anItem.option?.id ?? '')
										return line === undefined || quote === null
											? formatAmount(showKrw ? 'KRW' : 'USD', localLine(anItem))
											: formatAmount(quote.currency, line.lineAmount)
									})()}
								</span>
							</View>
						)
					})}
				</View>
			</View>
		</View>
	)
}
